import React, { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ActionToast from "../../../../shared/components/ActionToast";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface RfsShareButtonsProps {
  internalNumber: string;
  issue: string;
}

const RfsShareButtons = ({ internalNumber, issue }: RfsShareButtonsProps) => {
  const { translate } = useTranslation(["servicing"]);
  const [snackOpen, setSnackOpen] = useState(false);

  const currentUrl = window.location.href;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setSnackOpen(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = currentUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setSnackOpen(true);
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`RFS ${internalNumber}: ${issue}`);
    const body = encodeURIComponent(
      `${translate("servicing:rfsDetail.shareEmailBody")}: ${currentUrl}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `RFS ${internalNumber}`,
          text: issue,
          url: currentUrl,
        });
      } catch (err) {
        // User cancelled or share failed — no action needed
      }
    }
  };

  const hasWebShare = typeof navigator.share === "function";

  const iconBtnSx = {
    color: dt.textMuted,
    padding: "6px",
    transition: "all 0.15s ease",
    "&:hover": {
      color: dt.blue,
      backgroundColor: dt.blueGlow,
    },
  };

  return (
    <>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
        <Tooltip title={translate("servicing:rfsDetail.copyLink")} arrow>
          <IconButton size="small" onClick={handleCopyLink} sx={iconBtnSx}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={translate("servicing:rfsDetail.shareEmail")} arrow>
          <IconButton size="small" onClick={handleEmailShare} sx={iconBtnSx}>
            <EmailOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>

        {hasWebShare && (
          <Tooltip title={translate("servicing:rfsDetail.share")} arrow>
            <IconButton size="small" onClick={handleWebShare} sx={iconBtnSx}>
              <IosShareOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <ActionToast
        open={snackOpen}
        variant="success"
        message={translate("servicing:rfsDetail.linkCopied")}
        onClose={() => setSnackOpen(false)}
        duration={2000}
      />
    </>
  );
};

export default RfsShareButtons;
